import { defineStore } from 'pinia'
import { reactive } from 'vue'

type ToastLevel = 'success' | 'error' | 'warning' | 'info'

interface State {
    toasts: Array<Toast>
    visible: Array<Toast>
    timerPaused: boolean
}

interface Toast {
    id: number
    level: ToastLevel
    title: string
    message: string | null
    timeout: number
    closable?: boolean
    progress: number
    timer?: number
}

export const useToastStore = defineStore('toast', {
    state: (): State => ({
        toasts: <Toast[]>[],
        visible: <Toast[]>[],
        timerPaused: false,
    }),

    actions: {
        toastInfo(
            title: string,
            message?: string | null,
            timeout?: number | null,
            closable?: boolean | null
        ): number {
            return this.fire('info', title, message, timeout, closable)
        },

        toastSuccess(
            title: string,
            message?: string | null,
            timeout?: number | null,
            closable?: boolean | null
        ): number {
            return this.fire('success', title, message, timeout, closable)
        },

        toastWarning(
            title: string,
            message?: string | null,
            timeout?: number | null,
            closable?: boolean | null
        ): number {
            return this.fire('warning', title, message, timeout, closable)
        },

        toastError(
            title: string,
            message?: string | null,
            timeout?: number | null,
            closable?: boolean | null
        ): number {
            return this.fire('error', title, message, timeout, closable)
        },

        fire(
            level: ToastLevel,
            title: string,
            message?: string | null,
            timeout?: number | null,
            closable?: boolean | null
        ): number {
            const toast: Toast = reactive({
                id: Date.now() + Math.random(),
                level: level || 'info',
                title: title,
                message: message ?? null,
                timeout: timeout ?? 10000,
                closable: closable ?? true,
                progress: 100,
            })

            this.toasts.push(toast)

            setTimeout(() => {
                this.visible.push(toast)
                const timeShown = toast.timeout
                const start = new Date()
                const end = new Date(start.getTime() + timeShown)
                const totalDurationInMilliseconds = +end - +start
                let remainingMilliseconds = totalDurationInMilliseconds
                const loopMilliseconds = 100

                toast.timer = setInterval(() => {
                    if (this.timerPaused) {
                        return
                    }

                    remainingMilliseconds =
                        remainingMilliseconds - loopMilliseconds
                    toast.progress = Math.floor(
                        (100 * remainingMilliseconds)
                            / totalDurationInMilliseconds
                    )

                    if (remainingMilliseconds <= 0) {
                        clearInterval(toast.timer)
                        this.remove(toast.id)
                    }
                }, loopMilliseconds)
            }, 0)

            return toast.id
        },

        remove(id: number): void {
            const toast = this.toasts.find((toast) => toast.id === id)
            if (!toast) {
                return
            }

            if (toast.timer) {
                clearInterval(toast.timer)
                toast.timer = undefined
            }

            const visibleIndex = this.visible.indexOf(toast)
            if (visibleIndex !== -1) {
                this.visible.splice(visibleIndex, 1)
            }

            setTimeout(() => {
                const toastsIndex = this.toasts.indexOf(toast)
                if (toastsIndex !== -1) {
                    this.toasts.splice(toastsIndex, 1)
                }
            }, 1000)
        },
    },
})
